import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { orange } from '@mui/material/colors';
import { toast } from 'react-toastify';
import axios from 'axios';

interface ForgotPasswordProps {
  open: boolean;
  handleClose: () => void;
  initialEmail?: string;
}

const emailPattern = /\S+@\S+\.\S+/;
const INVALID_EMAIL_FORMAT_MESSAGE = 'The email address is not in a valid form (xxx@xxx.com)';

export default function ForgotPassword({ open, handleClose, initialEmail = '' }: ForgotPasswordProps) {
  const [loading, setLoading] = React.useState(false);
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [email, setEmail] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setEmail(initialEmail);
      setEmailError(false);
      setEmailErrorMessage('');
    }
  }, [open, initialEmail]);

  const resetForm = () => {
    setEmail('');
    setEmailError(false);
    setEmailErrorMessage('');
  };

  const handleCancel = () => {
    resetForm();
    handleClose();
  };

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError(true);
      setEmailErrorMessage('Please enter your email address.');
      return false;
    }

    if (!emailPattern.test(value)) {
      setEmailError(false);
      setEmailErrorMessage('');
      toast.error(INVALID_EMAIL_FORMAT_MESSAGE, { position: 'top-center' });
      return false;
    }

    setEmailError(false);
    setEmailErrorMessage('');
    return true;
  };

  const handlePasswordReset = async (event?: React.FormEvent) => {
    event?.preventDefault();

    const trimmedEmail = email.trim();
    if (!validateEmail(trimmedEmail)) {
      return;
    }

    setLoading(true);
    try {
      const csrfToken = await axios.get('/csrf');
      axios.defaults.headers.post['xsrfCookieName'] = 'csrftoken';
      axios.defaults.headers.post['X-CSRFToken'] = csrfToken.data.csrfToken;
      axios.defaults.headers.post['Content-Type'] = 'application/json';

      const response = await axios.post('/password-reset/', { email: trimmedEmail });
      resetForm();
      handleClose();
      toast.success(response?.data?.message, { position: 'top-center' });
    } catch (error: unknown) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message
        : 'An error occurred while sending the reset email.';
      toast.error(message, { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: handlePasswordReset,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${orange[700]}, ${orange[500]})` }} />

      <DialogTitle sx={{ pr: 6, pt: 2.5, pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(orange[50], 0.95),
              color: orange[800],
            }}
          >
            <MailOutlineIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Reset password
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Secure email recovery
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="Close reset password dialog"
          onClick={handleCancel}
          disabled={loading}
          sx={{ position: 'absolute', right: 12, top: 20 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
          Enter the email linked to your account. We&apos;ll send a time-limited link to choose a new password.
        </Typography>

        <TextField
          autoFocus
          variant="outlined"
          id="reset-email"
          name="reset-email"
          label="Work email"
          placeholder="name@company.com"
          type="text"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (emailError && event.target.value.trim()) {
              setEmailError(false);
              setEmailErrorMessage('');
            }
          }}
          error={emailError}
          helperText={emailErrorMessage}
          fullWidth
          disabled={loading}
          slotProps={{
            input: {
              sx: { borderRadius: 1.5 },
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
        <Button
          onClick={handleCancel}
          disabled={loading}
          sx={{ textTransform: 'none', minWidth: 96 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          loading={loading}
          loadingPosition="end"
          loadingIndicator={
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.95 }}>
              Sending...
              <CircularProgress variant="indeterminate" size={16} sx={{ color: 'white' }} />
            </Typography>
          }
          sx={{
            textTransform: 'none',
            backgroundColor: orange[800],
            fontWeight: 700,
            minWidth: 140,
            height: 44,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: orange[900],
              boxShadow: `0 6px 16px ${alpha(orange[800], 0.3)}`,
            },
          }}
        >
          {!loading && 'Send reset link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
